import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ExternalLink } from "lucide-react";
import { projectsAPI, mapProjectToProperty } from "../../services/api";

const GOLD = "#50C878";
const NAVY = "#013220";

// ─── AREA MAP EXPLORER (Google Maps) ─────────────────────────
// • Right column lists every UNIQUE area with its project count,
//   sorted by count DESC (e.g. "Kompally 10", "Miyapur 8" …).
// • Map drops an exact pin per project using its lat/lng.
// • No area selected  → ALL project pins shown, map fit to all.
// • An area selected   → only that area's pins shown, map fit to them.
// • Each pin's info window links to the project's google_map_link.
//
// The Google Maps JS API key is read from REACT_APP_GOOGLE_MAPS_API_KEY.
// ─────────────────────────────────────────────────────────────

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const HYD_CENTER = { lat: 17.4239, lng: 78.396 };

// Singleton loader so the script is injected only once.
let gmapsPromise = null;
function loadGoogleMaps(apiKey) {
  if (typeof window !== "undefined" && window.google && window.google.maps) {
    return Promise.resolve(window.google.maps);
  }
  if (gmapsPromise) return gmapsPromise;

  gmapsPromise = new Promise((resolve, reject) => {
    if (!apiKey) {
      reject(new Error("missing-key"));
      return;
    }
    const cb = "__gbf_gmaps_cb__";
    window[cb] = () => resolve(window.google.maps);
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${cb}`;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error("script-error"));
    document.head.appendChild(s);
  });
  return gmapsPromise;
}

export default function AreaMapExplorer() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedArea, setSelectedArea] = useState(null); // null = all
  const [mapStatus, setMapStatus] = useState("loading"); // loading | ready | error

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoRef = useRef(null);

  // 1. Load ALL projects (paginate generously to capture everything).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await projectsAPI.list({ limit: 500 });
        const rows = (res.data || []).map(mapProjectToProperty);
        if (alive) setProjects(rows);
      } catch (_) {
        if (alive) setProjects([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  // 2. Unique areas with counts, sorted by count DESC then name.
  const areas = useMemo(() => {
    const counts = {};
    for (const p of projects) {
      const a = (p.area || "").trim();
      if (!a) continue;
      counts[a] = (counts[a] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([area, count]) => ({ area, count }))
      .sort((a, b) => b.count - a.count || a.area.localeCompare(b.area));
  }, [projects]);

  // Projects that should currently be pinned.
  const visibleProjects = useMemo(() => {
    const withCoords = projects.filter(
      (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)
    );
    if (!selectedArea) return withCoords;
    return withCoords.filter((p) => (p.area || "").trim() === selectedArea);
  }, [projects, selectedArea]);

  // 3. Initialise the Google map once.
  useEffect(() => {
    let alive = true;
    loadGoogleMaps(GMAPS_KEY)
      .then((maps) => {
        if (!alive || !mapEl.current) return;
        mapRef.current = new maps.Map(mapEl.current, {
          center: HYD_CENTER,
          zoom: 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          styles: [
            { featureType: "poi.business", stylers: [{ visibility: "off" }] },
            { featureType: "transit", stylers: [{ visibility: "off" }] },
          ],
        });
        infoRef.current = new maps.InfoWindow();
        setMapStatus("ready");
      })
      .catch(() => alive && setMapStatus("error"));
    return () => { alive = false; };
  }, []);

  // 4. (Re)draw markers whenever the visible set changes & map is ready.
  const drawMarkers = useCallback(() => {
    const maps = window.google && window.google.maps;
    if (!maps || !mapRef.current) return;

    // Clear old markers.
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];

    const bounds = new maps.LatLngBounds();
    visibleProjects.forEach((p) => {
      const pos = { lat: p.lat, lng: p.lng };
      const marker = new maps.Marker({
        position: pos,
        map: mapRef.current,
        title: p.title,
        icon: {
          path: maps.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: GOLD,
          fillOpacity: 1,
          strokeColor: "#FFFFFF",
          strokeWeight: 2,
        },
      });

      marker.addListener("click", () => {
        const dirLink =
          p.googleMapLink ||
          `https://www.google.com/maps/search/?api=1&query=${p.lat},${p.lng}`;
        const html = `
          <div style="font-family:Inter,sans-serif;max-width:230px">
            <div style="font-weight:700;color:${NAVY};font-size:14px;margin-bottom:2px">${p.title}</div>
            <div style="color:#6b7280;font-size:12px;margin-bottom:6px">${p.builder || ""}${p.area ? " · " + p.area : ""}</div>
            <a href="${dirLink}" target="_blank" rel="noopener noreferrer"
               style="color:${GOLD};font-size:12px;font-weight:600;text-decoration:none">Open in Google Maps ↗</a>
          </div>`;
        infoRef.current.setContent(html);
        infoRef.current.open(mapRef.current, marker);
      });

      markersRef.current.push(marker);
      bounds.extend(pos);
    });

    // Fit map to the markers (or recentre on Hyderabad if none).
    if (visibleProjects.length === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(15);
    } else if (visibleProjects.length > 1) {
      mapRef.current.fitBounds(bounds, 60);
    } else {
      mapRef.current.setCenter(HYD_CENTER);
      mapRef.current.setZoom(11);
    }
  }, [visibleProjects]);

  useEffect(() => {
    if (mapStatus === "ready") drawMarkers();
  }, [mapStatus, drawMarkers]);

  function handleAreaClick(area) {
    setSelectedArea((cur) => (cur === area ? null : area)); // toggle off → show all
  }

  return (
    <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "stretch" }}>
      {/* ── MAP ──────────────────────────────────────── */}
      <div style={{ flex: "1 1 520px", minHeight: "460px", borderRadius: "12px", overflow: "hidden", border: "1px solid rgba(80,200,120,0.2)", position: "relative" }}>
        <div ref={mapEl} style={{ width: "100%", height: "100%", minHeight: "460px" }} />

        {/* Status overlays */}
        {mapStatus !== "ready" && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#EEE9DA", textAlign: "center", padding: "24px" }}>
            {mapStatus === "loading" && (
              <p style={{ fontFamily: "'Inter', sans-serif", color: "rgba(1,50,32,0.7)" }}>Loading map…</p>
            )}
            {mapStatus === "error" && (
              <div>
                <MapPin size={28} color={GOLD} style={{ marginBottom: "10px" }} />
                <p style={{ fontFamily: "'Inter', sans-serif", color: NAVY, fontWeight: 600, margin: "0 0 4px" }}>Map unavailable</p>
                <p style={{ fontFamily: "'Inter', sans-serif", color: "rgba(1,50,32,0.6)", fontSize: "0.85rem", margin: 0 }}>
                  Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in your environment to enable the interactive map. The area list still works.
                </p>
              </div>
            )}
          </div>
        )}

        {/* "Show all" reset chip when an area is selected */}
        {selectedArea && (
          <button
            onClick={() => setSelectedArea(null)}
            style={{ position: "absolute", top: "12px", left: "12px", zIndex: 5, background: "#FFFFFF", border: `1px solid ${GOLD}`, borderRadius: "20px", padding: "7px 14px", fontFamily: "'Inter', sans-serif", fontSize: "0.8rem", fontWeight: 600, color: NAVY, cursor: "pointer", boxShadow: "0 2px 10px rgba(0,0,0,0.12)" }}
          >
            ← Show all areas
          </button>
        )}
      </div>

      {/* ── AREA LIST (sorted by count desc) ───────────── */}
      <div style={{ flex: "1 1 300px", display: "flex", flexDirection: "column", gap: "10px", maxHeight: "520px", overflowY: "auto", paddingRight: "4px" }}>
        <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.3rem", color: NAVY, margin: "0 0 4px" }}>
          Browse by Area
        </h3>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.8rem", color: "rgba(1,50,32,0.55)", margin: "0 0 8px" }}>
          {selectedArea ? `Showing projects in ${selectedArea}` : "Showing all projects · Tap an area to filter"}
        </p>

        {areas.length === 0 && (
          <p style={{ fontFamily: "'Inter', sans-serif", color: "rgba(1,50,32,0.6)", fontSize: "0.88rem" }}>Loading areas…</p>
        )}

        {areas.map(({ area, count }) => {
          const active = selectedArea === area;
          return (
            <button
              key={area}
              onClick={() => handleAreaClick(area)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "13px 16px",
                background: active ? "rgba(80,200,120,0.16)" : "rgba(80,200,120,0.05)",
                border: `1px solid ${active ? GOLD : "rgba(80,200,120,0.2)"}`,
                borderRadius: "8px",
                color: NAVY,
                cursor: "pointer",
                fontFamily: "'Inter', sans-serif",
                fontSize: "0.9rem",
                fontWeight: active ? 700 : 500,
                transition: "background 0.2s, border-color 0.2s",
              }}
              onMouseOver={(e) => { if (!active) { e.currentTarget.style.background = "rgba(80,200,120,0.1)"; e.currentTarget.style.borderColor = "rgba(80,200,120,0.5)"; } }}
              onMouseOut={(e) => { if (!active) { e.currentTarget.style.background = "rgba(80,200,120,0.05)"; e.currentTarget.style.borderColor = "rgba(80,200,120,0.2)"; } }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <MapPin size={14} color={GOLD} /> {area}
              </span>
              <span style={{ background: active ? NAVY : GOLD, color: "#FFFFFF", borderRadius: "12px", padding: "2px 11px", fontSize: "0.78rem", fontWeight: 700, minWidth: "26px", textAlign: "center" }}>
                {count}
              </span>
            </button>
          );
        })}

        {selectedArea && (
          <button
            onClick={() => navigate(`/properties?area=${encodeURIComponent(selectedArea)}`)}
            style={{ marginTop: "6px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "12px 16px", background: "linear-gradient(135deg, #50C878, #7FD9A0)", border: "none", borderRadius: "8px", color: "#FFFFFF", fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "0.88rem", cursor: "pointer" }}
          >
            View {selectedArea} listings <ExternalLink size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
